import { Check, CheckCheck, Clock, MessageSquareX, Send } from 'lucide-react';
import type { SMSStatistic } from 'types/sms.type';
import {
    formatCount,
    formatStatusPercent,
    type CallStatusSummarySubStatus,
    type StatusIconTheme,
} from '../../CallAnalytics/CallStatusSummary/callStatusSummary.utils';

export type SMSStatusSummaryCardVariant = 'queued' | 'sending' | 'sent' | 'delivered' | 'failed';

export type SMSStatusSummarySubStatus = CallStatusSummarySubStatus;

export type SMSStatusSummaryCard = {
    id: SMSStatusSummaryCardVariant;
    label: string;
    count: number;
    percent: number;
    variant: SMSStatusSummaryCardVariant;
    tooltip: string;
    breakdownTitle: string;
    breakdownSubtitle: string;
    subStatuses: SMSStatusSummarySubStatus[];
};

export const SMS_STATUS_ICON_THEME: Record<SMSStatusSummaryCardVariant, StatusIconTheme> = {
    queued: {
        icon: Clock,
        color: 'var(--slai-ui-primary-purple-dark)',
        softBackground: 'var(--slai-ui-primary-purple-light)',
    },
    sending: {
        icon: Send,
        color: 'var(--slai-ui-primary-purple-dark)',
        softBackground: 'var(--slai-ui-primary-purple-light)',
    },
    sent: {
        icon: Check,
        color: 'var(--slai-ui-primary-purple-dark)',
        softBackground: 'var(--slai-ui-primary-purple-light)',
    },
    delivered: {
        icon: CheckCheck,
        color: 'var(--slai-ui-primary-purple-dark)',
        softBackground: 'var(--slai-ui-primary-purple-light)',
    },
    failed: {
        icon: MessageSquareX,
        color: 'var(--slai-ui-primary-purple-dark)',
        softBackground: 'var(--slai-ui-primary-purple-light)',
    },
};

/**
 * Appended to every card whose percentage runs off getSentSMSTotal. Shared rather than
 * repeated so the four cannot drift into describing the same denominator differently.
 */
const RATE_BASIS_NOTE =
    'The percentage is a share of messages already sent to a carrier, not of every message created, so anything still waiting in the queue is excluded until it goes out.';

const asCount = (value: number | null | undefined): number => value ?? 0;

const normalizeSMSStatistic = (smsStats?: SMSStatistic): SMSStatistic => ({
    pending: asCount(smsStats?.pending),
    queued: asCount(smsStats?.queued),
    overDue: asCount(smsStats?.overDue),
    sending: asCount(smsStats?.sending),
    retrying: asCount(smsStats?.retrying),
    sent: asCount(smsStats?.sent),
    delivered: asCount(smsStats?.delivered),
    failed: asCount(smsStats?.failed),
    failureBreakdown: smsStats?.failureBreakdown ?? [],
    total: asCount(smsStats?.total),
});

/**
 * Failure segments for the Failed card, newest API first.
 *
 * Percentages come from the backend rather than being recomputed here: they are rounded
 * by largest remainder so the bar fills exactly, which per-segment rounding does not
 * guarantee. Falls back to the old failed-vs-not split when the field is missing, so the
 * panel still renders against an API that predates it.
 */
const buildFailureSubStatuses = (stats: SMSStatistic, sentTotal: number): SMSStatusSummarySubStatus[] => {
    const breakdown = stats.failureBreakdown ?? [];
    if (breakdown.length === 0) {
        return buildSubStatuses(
            [
                { id: 'failed', label: 'Failed', count: stats.failed },
                { id: 'succeeded', label: 'No failure', count: Math.max(0, sentTotal - stats.failed) },
            ],
            sentTotal
        );
    }

    return breakdown.map((reason) => ({
        id: reason.code,
        label: reason.label,
        count: reason.count,
        percent: reason.percent,
    }));
};

/**
 * Messages already handed to a carrier, which is the only honest base for a delivery or
 * failure rate: anything still in the queue has had no chance to succeed or fail.
 *
 * Subtracts `queued` alone. `pending` and `overDue` are the two halves of it, not further
 * buckets, so subtracting all three removes the queue twice and can drive this negative.
 */
export const getSentSMSTotal = (smsStats?: SMSStatistic): number => {
    const stats = normalizeSMSStatistic(smsStats);
    return Math.max(0, stats.total - stats.queued);
};

const buildSubStatuses = (
    entries: Array<{ id: string; label: string; count: number }>,
    categoryTotal: number
): SMSStatusSummarySubStatus[] =>
    entries.map((entry) => ({
        id: entry.id,
        label: entry.label,
        count: entry.count,
        percent: formatStatusPercent(entry.count, categoryTotal),
    }));

export const buildSMSStatusSummaryCards = (smsStats?: SMSStatistic): SMSStatusSummaryCard[] => {
    const stats = normalizeSMSStatistic(smsStats);
    // Delivery rates are a share of the messages that already left the queue.
    const sentTotal = getSentSMSTotal(stats);
    const percent = (count: number) => formatStatusPercent(count, sentTotal);

    // `queued` is already the whole queue; `pending` and `overDue` are its two halves.
    const queuedTotal = stats.queued;
    const sendingTotal = stats.sending + stats.retrying;
    const acceptedTotal = stats.sent + stats.delivered;

    return [
        {
            id: 'queued',
            label: 'Queued / Overdue',
            count: queuedTotal,
            // Not sent yet, so a rate against sent messages is not meaningful.
            percent: 0,
            variant: 'queued',
            tooltip:
                'Waiting in our send queue. Nothing has been handed to the messaging provider yet. Queued — scheduled for today. Overdue — scheduled for prior days and still not sent.',
            breakdownTitle: 'Queued / Overdue breakdown',
            breakdownSubtitle: `Split of ${formatCount(queuedTotal)} messages waiting to be sent`,
            subStatuses: buildSubStatuses(
                [
                    { id: 'queued', label: 'Queued', count: stats.queued },
                    { id: 'pending', label: 'Pending', count: stats.pending },
                    { id: 'overdue', label: 'Overdue', count: stats.overDue },
                ],
                queuedTotal
            ),
        },
        {
            id: 'sending',
            label: 'Sending',
            count: sendingTotal,
            percent: percent(sendingTotal),
            variant: 'sending',
            tooltip: `Accepted by the messaging provider, which has not yet reported passing it to a mobile carrier. Retrying — a send attempt failed and will be repeated. ${RATE_BASIS_NOTE}`,
            breakdownTitle: 'Sending breakdown',
            breakdownSubtitle: `Split of ${formatCount(sendingTotal)} messages in flight`,
            subStatuses: buildSubStatuses(
                [
                    { id: 'sending', label: 'Sending', count: stats.sending },
                    { id: 'retrying', label: 'Retrying', count: stats.retrying },
                ],
                sendingTotal
            ),
        },
        {
            id: 'sent',
            label: 'Sent',
            count: stats.sent,
            percent: percent(stats.sent),
            variant: 'sent',
            tooltip: `The provider passed the message to the mobile carrier and the carrier accepted it. Still waiting on a delivery receipt from the handset. ${RATE_BASIS_NOTE}`,
            breakdownTitle: 'Sent breakdown',
            breakdownSubtitle: `Delivery receipts for ${formatCount(acceptedTotal)} accepted messages`,
            subStatuses: buildSubStatuses(
                [
                    { id: 'awaitingReceipt', label: 'Awaiting receipt', count: stats.sent },
                    { id: 'delivered', label: 'Delivered', count: stats.delivered },
                ],
                acceptedTotal
            ),
        },
        {
            id: 'delivered',
            label: 'Delivered',
            count: stats.delivered,
            percent: percent(stats.delivered),
            variant: 'delivered',
            tooltip: `Messages confirmed as delivered to the contact handset. ${RATE_BASIS_NOTE}`,
            breakdownTitle: 'Delivered breakdown',
            breakdownSubtitle: `Delivered share of ${formatCount(sentTotal)} sent messages`,
            subStatuses: buildSubStatuses(
                [
                    { id: 'delivered', label: 'Delivered', count: stats.delivered },
                    {
                        id: 'notDelivered',
                        label: 'Not delivered',
                        count: Math.max(0, sentTotal - stats.delivered),
                    },
                ],
                sentTotal
            ),
        },
        {
            id: 'failed',
            label: 'Failed',
            count: stats.failed,
            percent: percent(stats.failed),
            variant: 'failed',
            tooltip: `Messages the carrier could not deliver — invalid or landline numbers, blocked contacts and carrier errors. ${RATE_BASIS_NOTE} Click to see which carrier errors caused them.`,
            breakdownTitle: 'Failed breakdown',
            // Names the denominator explicitly. The card above reads a share of sent
            // messages and these read a share of failures, so two bases sit side by side.
            breakdownSubtitle: `Why ${formatCount(stats.failed)} messages failed`,
            subStatuses: buildFailureSubStatuses(stats, sentTotal),
        },
    ];
};
